import React from 'react';
import { Link } from 'react-router-dom';
import {
  GripVertical,
  ChevronRight,
  ChevronDown,
  Folder,
  FileText,
  BookOpen,
  Plus,
  Trash2,
  Edit2,
  ExternalLink,
  Pin,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { HelpCenterStructureNode } from '@/types';
import { useTranslation } from 'react-i18next';
import type { FlatData } from './structureTreeUtils';
import { getNodePath, getParentPathSegments } from './structureTreeUtils';

interface StructureTreeItemProps {
  itemId: string;
  node: HelpCenterStructureNode;
  childrenIds: string[];
  level: number;
  isExpanded: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  itemProps: React.HTMLAttributes<HTMLDivElement>;
  helpCenterId: string;
  helpCenterSlug: string;
  flatData: FlatData;
  onToggleExpand: (itemId: string, isExpanded: boolean) => void;
  onOpenHelpCenter: (slug: string, path: string) => void;
  onAddCategory: (parentId: number | null) => void;
  onEditCategory: (category: any) => void;
  onDeleteNode: (node: HelpCenterStructureNode) => void;
  onAddArticle?: (categoryId: number | null) => void;
  onAddGuide?: (categoryId: number | null) => void;
  onEditItem?: (item: any) => void;
  onTogglePin?: (node: HelpCenterStructureNode) => void;
  isPinned?: boolean;
}

export function StructureTreeItem({
  itemId,
  node,
  childrenIds,
  level,
  isExpanded,
  isDragging,
  isDragOver,
  itemProps,
  helpCenterId,
  helpCenterSlug,
  flatData,
  onToggleExpand,
  onOpenHelpCenter,
  onAddCategory,
  onEditCategory,
  onDeleteNode,
  onAddArticle,
  onAddGuide,
  onEditItem,
  onTogglePin,
  isPinned = false,
}: StructureTreeItemProps) {
  const { t } = useTranslation();
  const isCategory = node.type === 'category';
  const isItem = node.type === 'item';
  const hasChildren = childrenIds.length > 0;

  // Get display info
  const { title, icon, isUnpublished, statusLabel } = React.useMemo(() => {
    let title = '';
    let icon: LucideIcon = Folder;
    let isUnpublished = false;
    let statusLabel: string | null = null;

    if (isCategory) {
      title = node.category!.title;
      icon = Folder;
      isUnpublished = !node.category!.is_published;
      if (isUnpublished) statusLabel = 'Hidden';
    } else if (isItem) {
      const notActive = !node.item!.is_published; // "Active" toggle off
      if (node.item!.item_type === 'article') {
        title = node.item!.article?.title || `Article #${node.item!.item_id}`;
        icon = FileText;
        const article: any = node.item!.article;
        // New draft-mode articles are hidden until first published (even when Active).
        const isDraftNeverPublished = !!article?.uses_drafts && !article?.published_at;
        isUnpublished = notActive || isDraftNeverPublished;
        statusLabel = isDraftNeverPublished ? 'Draft' : notActive ? 'Hidden' : null;
      } else if (node.item!.item_type === 'guide') {
        title = node.item!.guide?.title || `Guide #${node.item!.item_id}`;
        icon = BookOpen;
        isUnpublished = notActive;
        statusLabel = notActive ? 'Hidden' : null;
      }
    }

    return { title, icon, isUnpublished, statusLabel };
  }, [node, isCategory, isItem]);

  // Calculate path for external link
  const nodePath = React.useMemo(() => {
    const parentSegments = getParentPathSegments(itemId, flatData);
    return getNodePath(node, parentSegments);
  }, [itemId, node, flatData]);

  return (
    <div
      className={`group flex items-center gap-2 p-2 rounded-md hover:bg-accent transition-colors ${
        isDragOver ? 'ring-2 ring-primary ring-offset-2 bg-accent' : ''
      } ${isDragging ? 'opacity-50' : ''}`}
      style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
      {...itemProps}
    >
      {/* Drag handle */}
      <div className="cursor-grab active:cursor-grabbing touch-none">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Expand/collapse button */}
      {isCategory && hasChildren ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(itemId, isExpanded);
          }}
          className="flex items-center"
          type="button"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      ) : (
        <div className="w-4" />
      )}

      {/* Icon */}
      {React.createElement(icon, {
        className: `h-4 w-4 ${isUnpublished ? 'text-muted-foreground opacity-50' : 'text-muted-foreground'}`,
      })}

      {/* Title */}
      <span
        className={`flex-1 text-sm font-medium ${isUnpublished ? 'opacity-50 text-muted-foreground' : ''}`}
      >
        {node.item?.item_type === 'article' ? (
          <Link to={`/help-centers/${helpCenterId}/items/${node.item!.id}/edit`}>
            {title}
          </Link>
        ) : (
          title
        )}
      </span>

      {/* Publish-status indicator */}
      {statusLabel && (
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${
            statusLabel === 'Draft'
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-muted text-muted-foreground'
          }`}
          title={statusLabel === 'Draft' ? 'Not published yet — hidden from readers' : 'Hidden from the help center'}
        >
          {statusLabel}
        </span>
      )}

      {isPinned && (
        <Pin className="h-3 w-3 text-primary fill-primary/20" />
      )}

      {/* Item type badge */}
      {isItem && (
        <span className="text-xs text-muted-foreground capitalize">
          {node.item!.item_type}
        </span>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onTogglePin && (
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 w-7 p-0 ${isPinned ? 'text-primary' : ''}`}
            onClick={() => onTogglePin(node)}
            title={isPinned ? t('helpCenter.highlights.removeTooltip') : t('helpCenter.highlights.addTooltip')}
            type="button"
          >
            <Pin className={`h-3 w-3 ${isPinned ? 'fill-primary/20' : ''}`} />
          </Button>
        )}

        {/* Open in new window */}
        {!isUnpublished && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onOpenHelpCenter(helpCenterSlug, nodePath)}
            title="Open in new window"
            type="button"
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        )}

        {/* Category actions */}
        {isCategory && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  title="Add to category"
                  type="button"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {onAddArticle && (
                  <DropdownMenuItem onClick={() => onAddArticle(node.category!.id)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Add Article
                  </DropdownMenuItem>
                )}
                {onAddGuide && (
                  <DropdownMenuItem onClick={() => onAddGuide(node.category!.id)}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Add Guide
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onAddCategory(node.category!.id)}>
                  <Folder className="h-4 w-4 mr-2" />
                  Add Category
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onEditCategory(node.category!)}
              title="Edit category"
              type="button"
            >
              <Edit2 className="h-3 w-3" />
            </Button>
          </>
        )}

        {/* Item edit button */}
        {isItem && onEditItem && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onEditItem(node.item!)}
            title={`Edit ${node.item!.item_type}`}
            type="button"
          >
            <Edit2 className="h-3 w-3" />
          </Button>
        )}

        {/* Delete button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
          onClick={() => onDeleteNode(node)}
          title={`Delete ${isCategory ? 'category' : 'item'}`}
          type="button"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
